import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  BarChart3, 
  Grid, 
  FileText, 
  Settings, 
  Activity,
  ChevronDown,
  Building2,
  Calendar,
  User
} from 'lucide-react';
import { Button } from '../ui/button.js';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip.js';

interface WealthOSSidebarProps {
  isCollapsed?: boolean;
}

const WealthOSSidebar: React.FC<WealthOSSidebarProps> = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      path: '/wealthos/dashboard',
      tooltip: 'WealthOS Dashboard - Overview and metrics'
    },
    {
      id: 'smartgrid',
      label: 'SmartGrid',
      icon: Grid,
      path: '/wealthos/smartgrid',
      tooltip: 'SmartGrid - Intelligent spreadsheet interface'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: FileText,
      path: '/wealthos/reports',
      tooltip: 'Reports - Generate and download tax reports'
    },
    {
      id: 'forms',
      label: 'Forms',
      icon: FileText,
      path: '/wealthos/forms',
      tooltip: 'Forms - View and manage tax forms'
    },
    {
      id: 'oversight',
      label: 'Oversight',
      icon: Activity,
      path: '/wealthos/oversight',
      tooltip: 'Oversight - Monitor Bee health and circuits'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      path: '/wealthos/settings',
      tooltip: 'Settings - Configure WealthOS preferences'
    }
  ];

  const isActive = (path: string) => location.pathname === path;

  const renderNavigationButton = (item: typeof navigationItems[0]) => {
    const IconComponent = item.icon;
    const active = isActive(item.path);

    return (
      <Tooltip key={item.id}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size={isCollapsed ? "icon" : "default"}
            onClick={() => navigate(item.path)}
            className={`
              w-full justify-start gap-3 h-11 px-3 transition-all duration-200
              ${active 
                ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30 shadow-lg shadow-teal-500/10' 
                : 'text-slate-300 hover:text-white hover:bg-slate-700/50 border border-transparent'
              }
              ${isCollapsed ? 'px-2' : ''}
            `}
          >
            <IconComponent className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} flex-shrink-0`} />
            {!isCollapsed && (
              <span className="font-medium">{item.label}</span>
            )}
          </Button>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" className="ml-2">
            <p>{item.tooltip}</p>
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  return (
    <TooltipProvider>
      <div className={`
        h-full bg-slate-900/95 backdrop-blur-xl border-r border-slate-700/50 flex flex-col
        ${isCollapsed ? 'w-16' : 'w-64'}
        transition-all duration-300 ease-in-out
      `}>
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-slate-700/50">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-3"
          >
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <Building2 className="w-6 h-6 text-teal-400" />
            </div>
            {!isCollapsed && (
              <div>
                <h2 className="text-lg font-bold text-white">WealthOS</h2>
                <p className="text-xs text-slate-400">Agentic Control Hub</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Top Navigation Controls */}
        {!isCollapsed && (
          <div className="flex-shrink-0 p-4 space-y-3 border-b border-slate-700/50">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <select className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500">
                <option value="sic-advisors">SIC Advisors LLC</option>
                <option value="client-entity-1">Client Entity 1</option>
                <option value="client-entity-2">Client Entity 2</option>
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <select className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500">
                <option value="2025">2025</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </div>
            
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              <span className="flex-1 text-sm text-slate-300">Dana Patterson</span>
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <div className="flex-1 p-4 space-y-2 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="space-y-2"
          >
            {navigationItems.map(renderNavigationButton)}
          </motion.div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t border-slate-700/50">
          {!isCollapsed && (
            <div className="text-xs text-slate-400 space-y-1">
              <div className="flex items-center justify-between">
                <span>BeeMemory</span>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex items-center justify-between">
                <span>ARCOS</span>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex items-center justify-between">
                <span>Dropbox Sync</span>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default WealthOSSidebar;
